import { ChangeDetectionStrategy, Component, inject, OnInit, ViewChild } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { CodeInputComponent } from "angular-code-input";
import { BehaviorSubject, EMPTY, Observable, Subject } from "rxjs";
import { catchError, finalize, switchMap, tap } from "rxjs/operators";

import { AuthModel } from "@intocare/auth-data";
import { SnackbarService } from "@intocare/ui";
import { CONTEXT_TOKEN } from "@intocare/util";

@Component({
	selector: "ic-mfa-setup",
	templateUrl: "./mfa-setup.component.html",
	styleUrls: ["./mfa-setup.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class MfaSetupComponent implements OnInit {
	private readonly authModel = inject(AuthModel);
	private readonly formBuilder = inject(UntypedFormBuilder);
	private readonly route = inject(ActivatedRoute);
	private readonly router = inject(Router);
	private readonly snackbar = inject(SnackbarService);

	private readonly contextService = inject(CONTEXT_TOKEN);

	@ViewChild("codeInput") codeInput!: CodeInputComponent;
	fb: UntypedFormBuilder;
	form: UntypedFormGroup = this.formBuilder.group({
		code: [""],
	});
	code = "";
	mfaNotEnabled: Observable<boolean> = new Subject<boolean>().asObservable();
	stringQrCode: string;
	secretCode: string;
	showCode: boolean;
	private _invalidCodeSubject: Subject<boolean> = new Subject<boolean>();
	invalidCode: Observable<boolean> = this._invalidCodeSubject.asObservable();
	private _toShortCodeSubject: Subject<boolean> = new Subject<boolean>();
	toShortCode: Observable<boolean> = this._toShortCodeSubject.asObservable();
	showInstructions$ = new BehaviorSubject(false);
	showSuccessful$ = new BehaviorSubject(false);
	loadingInstructions$ = new BehaviorSubject(true);
	loadingSuccessful$ = new BehaviorSubject(false);
	connectionFailed$ = new BehaviorSubject(false);
	refreshing$ = new BehaviorSubject(false);
	codeRequired: boolean;

	ngOnInit(): void {
		this.generateCode();
	}

	generateCode(): void {
		if (!this.stringQrCode) {
			this.authModel
				.setupMFA()
				.pipe(
					tap(({ secretCode, username }) => {
						this.secretCode = secretCode;
						this.stringQrCode = `otpauth://totp/into.care:${username}?secret=${secretCode}&issuer=into.care`;
						this.showInstructions$.next(true);
						this.loadingInstructions$.next(false);
					}),
					catchError(() => {
						this.loadingInstructions$.next(false);
						this.connectionFailed$.next(true);
						this.snackbar.show({ text: "auth.mfa_fail", error: true });
						return EMPTY;
					}),
				)
				.subscribe();
		}
	}

	onCodeCompleted(code: string) {
		this.code = code;
		this.validate();
	}

	onCodeChanged(code: string) {
		this.code = code;
		this._invalidCodeSubject.next(false);
		this._toShortCodeSubject.next(false);
		this.codeRequired = false;
	}

	onLabelClick() {
		this.codeInput.focusOnField(this.code.length);
	}

	validate() {
		this.loadingSuccessful$.next(true);
		this.codeRequired = this.code === "";
		if (!isNaN(Number(this.code)) && this.code.length == 6) {
			this.save();
		} else if (!this.codeRequired) {
			this._toShortCodeSubject.next(true);
			this.loadingSuccessful$.next(false);
		} else {
			this._toShortCodeSubject.next(false);
			this._invalidCodeSubject.next(false);
			this.loadingSuccessful$.next(false);
		}
	}

	goToLogin() {
		this.route.root.firstChild?.paramMap.subscribe((params) => {
			if (!params.get("home")) {
				this.router.navigate([params.get("org"), "sign-in-with-username"]).then(() => {
					location.reload();
				});
			} else {
				this.router.navigate([params.get("org"), params.get("home"), "sign-in-with-username"]).then(() => {
					location.reload();
				});
			}
		});
	}

	cancel() {
		this.authModel
			.logOut(false, { redirect: false })
			.pipe(
				finalize(() => {
					this.goToLogin();
				}),
			)
			.subscribe();
	}

	refreshPage() {
		this.refreshing$.next(true);
		location.reload();
	}

	save(): void {
		this.authModel
			.verifyTokenAndEnableMFA(this.code)
			.pipe(
				switchMap(() => this.authModel.logOut()),
				tap(() => {
					this.loadingSuccessful$.next(false);
					this.showInstructions$.next(false);
					this.showSuccessful$.next(true);
				}),
				catchError((err: unknown) => {
					this._invalidCodeSubject.next(true);
					this.loadingSuccessful$.next(false);
					this.snackbar.show({ text: err as string, error: true });
					return EMPTY;
				}),
			)
			.subscribe();
	}
}
